import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { unparse } from 'papaparse';
import { MoreHorizontal, PencilLine, ListFilter, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ui/dialog.tsx';
import { Button } from '@ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@ui/dropdown-menu.tsx';
import { SegmentMembersTable } from './segment-members-table.tsx';
import { apiAdminsegmentsIdmembersGetCollection } from '@r/api/generated/client.ts';
import type { SegmentSegmentCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { downloadCSV } from '@r/utils/utils.ts';
import { useDeleteSegment } from '@admin/hooks/use-delete-segment.ts';

type Segment = SegmentSegmentCollectionReadModel;

interface SegmentPreviewDialogProps {
  segment: Segment | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onEditDetails: (segment: Segment) => void;
  onManageRules: (segment: Segment) => void;
  onDeleted?: () => void;
}

/** Modale de prévisualisation d'un segment : détails, utilisateurs correspondants et export CSV. */
export const SegmentPreviewDialog: FC<SegmentPreviewDialogProps> = ({
  segment,
  isOpen,
  onOpenChange,
  onEditDetails,
  onManageRules,
  onDeleted
}) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isExporting, setIsExporting] = useState(false);

  const { handleDelete } = useDeleteSegment(() => {
    onOpenChange(false);
    onDeleted?.();
  });

  /**
   * Ferme la modale puis exécute l'action (navigation ou ouverture d'une autre modale), en
   * réinitialisant `pointer-events` sur le body : Radix peut le laisser bloqué à "none" quand
   * une modale contenant un menu ouvert se ferme en même temps qu'on navigue ailleurs.
   */
  const closeAndNavigate = (action: () => void) => {
    onOpenChange(false);
    document.body.style.pointerEvents = '';
    action();
    setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 0);
  };

  const handleExport = async () => {
    if (!segment) return;

    setIsExporting(true);

    try {
      const limit = 500;
      const firstPage = await apiAdminsegmentsIdmembersGetCollection(segment.id, {
        page: 1,
        perPage: limit
      });
      const firstPageMembers = firstPage.data ?? [];

      const totalPageCount = Math.ceil(firstPage.pagination.totalItems / limit);

      const remainingPages = await Promise.all(
        Array.from({ length: Math.max(totalPageCount - 1, 0) }, (_, i) =>
          apiAdminsegmentsIdmembersGetCollection(segment.id, { page: i + 2, perPage: limit })
        )
      );

      const allMembers = [
        ...firstPageMembers,
        ...remainingPages.flatMap((page) => page.data ?? [])
      ];

      const fields = [
        t('admin.segments.members.column.fullname'),
        t('admin.segments.members.column.email'),
        t('admin.segments.members.column.unique.code'),
        t('admin.segments.members.column.company')
      ];

      const csv = unparse({
        fields,
        data: allMembers.map((member) => [
          member.user.fullname,
          member.user.email,
          member.uniqueCode ?? '',
          member.company?.name ?? ''
        ])
      });

      const fileName = `segment-${segment.id}-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
      downloadCSV(csv, fileName);
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/segments')) {
        showOrvalErrorToast(error, t('admin.segments.export.error'));
      } else {
        toast.error(t('admin.segments.export.error'));
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-7xl overflow-y-auto p-10 !rounded-3xl">
        {segment && (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between gap-2">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    {t('admin.segments.preview.dialog.label')}
                  </p>
                  <DialogTitle className="text-2xl">{segment.name}</DialogTitle>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="h-8 w-8 shrink-0 p-0">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => closeAndNavigate(() => onEditDetails(segment))}
                    >
                      <PencilLine className="mr-2 w-4 h-4" />
                      {t('admin.segments.action.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => closeAndNavigate(() => onManageRules(segment))}
                    >
                      <ListFilter className="mr-2 w-4 h-4" />
                      {t('admin.segments.action.rules')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDelete(segment)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 w-4 h-4" />
                      {t('admin.segments.action.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {segment.description && (
                <p className="text-muted-foreground">{segment.description}</p>
              )}
            </DialogHeader>

            <SegmentMembersTable
              segmentId={segment.id}
              onExport={handleExport}
              isExporting={isExporting}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
